import { DocumentReference, Timestamp } from 'firebase/firestore'

// Enum for Championship Path based on the Python Pydantic model
export enum ChampionshipPath {
  ALTERNATE_LEAD = 'Alternate Lead',
  BONUS_LEAD = 'Bonus Lead',
  LEAD = 'Lead',
  ALTERNATE_RHYTHM = 'Alternate Rhythm',
  BONUS_RHYTHM = 'Bonus Rhythm',
  RHYTHM = 'Rhythm',
  BONUS_BASS = ' Bass', // Note the space before Bass as in the Python model
  BASS = 'Bass',
}

// Enum for Status based on the Python Pydantic model
export enum Status {
  RECOGNIZED = 'RECOGNIZED',
  NOT_RECOGNIZED = 'NOT_RECOGNIZED',
  DOWNLOAD_FAILED = 'DOWNLOAD_FAILED',
  ERROR = 'ERROR',
  SCRAPED = 'SCRAPED',
  MANUALLY_EDITED = 'MANUALLY_EDITED',
  AUTO_APPROVED = 'AUTO_APPROVED',
  PUSHED = 'PUSHED',
  NEEDS_CLASSIFICATION = 'NEEDS_CLASSIFICATION',
  CLASSIFIED = 'CLASSIFIED',
  CLASSIFICATION_FAILED = 'CLASSIFICATION_FAILED',
  MANUALLY_APPROVED = 'MANUALLY_APPROVED', // Added from your previous example
  INVALID_SCORE = 'INVALID_SCORE', // Added from your previous example
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

// Define the option type for the Select component
export interface SelectOption {
  label: string
  value: string
}

// Define the context type for ScoresContext
export interface ScoresContextType {
  scores: ScoreWithRef[]
  setScores: React.Dispatch<React.SetStateAction<ScoreWithRef[]>>
  filteredScores: ScoreWithRef[]
  pages: string[]
  changeSelectedStatuses: (
    selected: SelectOption[],
    setStateFunction: React.Dispatch<React.SetStateAction<SelectOption[]>>
  ) => void
  changeSelectedPages: (selected: SelectOption[]) => void
  applyFilters: () => void
  statusOptions: SelectOption[]
  approvedScores: ScoreWithRef[]
  pushedScores: ScoreWithRef[]
}

// Song document type based on the actual championshipSongs array structure
export interface SongDocument {
  artist: string
  level: string
  path: string
  song: string
  // Add any other optional fields that might exist
  arrangement?: string
  difficulty?: number
}

// Week document type based on the screenshot
export interface WeekDocument {
  // Fields visible in the screenshot
  championshipSongs: SongDocument[]
  lastScraped: Timestamp | string
  numPages: number
  url: string
  weekNum: number | string

  // Other potential fields that might exist
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

// Score document type with all fields from the provided example and Pydantic model
export interface ScoreDocument {
  // Required fields
  uniqueId: string
  page: number
  status: Status | string // Using the Status enum

  // Fields from the example and Pydantic model
  absoluteScore: number
  accuracy: number | null
  author: string
  classificationMethod?: string
  classifiedArtist?: string
  classifiedArtistConfidence?: string
  classifiedLevel?: string | null
  classifiedSong?: string
  classifiedSongConfidence?: string
  commentId: number
  datetime: Timestamp
  detectedText: string | null
  detectedTextRaw: string | null
  extractedArtist: string | null
  extractedSong: string | null
  image: string // This corresponds to image_url in the Pydantic model
  imageIx: number
  imageKey: string
  ix: number
  language: string | null
  link: string
  longestStreak: number | null
  numPlays: number | null
  path: ChampionshipPath | string | null // Using the ChampionshipPath enum
  recognitionStatusNotes: string
  statusBeforePush?: string

  // Additional fields that might be optional
  inputSong?: string
  inputArtist?: string
  inputPath?: string
  inputAccuracy?: number
  inputStreak?: number
  inputAbsoluteScore?: number
}

// Score with reference (as returned from Firestore)
export interface ScoreWithRef {
  ref: DocumentReference

  // Required fields
  uniqueId: string
  page: number
  status: Status | string

  // Core score data
  absoluteScore: number | null
  accuracy: number | null
  author: string
  commentId: number
  datetime: Timestamp
  image: string
  imageIx: number
  imageKey: string
  ix: number
  link: string
  longestStreak: number | null
  numPlays: number | null
  path: ChampionshipPath | string | null
  recognitionStatusNotes: string

  // Classification related fields
  classificationMethod?: string
  classifiedArtist?: string | null
  classifiedArtistConfidence?: string | null
  classifiedLevel?: string | null
  classifiedSong?: string | null
  classifiedSongConfidence?: string | null
  classified_level?: string | null
  level?: string | null

  // Text detection fields
  detectedText: string | null
  detectedTextRaw: string | null
  extractedArtist: string | null
  extractedSong: string | null
  language: string | null

  // User input fields
  inputAbsoluteScore?: string
  inputAccuracy?: string
  inputArtist?: string
  inputLevel?: string | null
  inputLongestStreak?: string
  inputPath?: string
  inputSong?: string
  inputStreak?: number

  // Approval and editing fields
  lastApprovedBy?: string
  lastApprovedDate?: string
  lastEditedBy?: string
  statusBeforePush?: string
}

// Define the ScoreState interface
export interface ScoreState {
  value: any // Cambiato da string a any per supportare StateValue
  matches: (state: string) => boolean
  hasTag: (tag: string) => boolean
  getMeta: () => Record<string, any>
  [key: string]: any // For any additional properties in the state
}
